import { Dispatch, SetStateAction, useState } from 'react'
import { Table, TableBody, TableContainer, useMediaQuery, useTheme } from '@mui/material'

import {
  IConsumerScanRespDto,
  IScanToEarnCreateAccountOrProductDto,
  IScanToEarnSettingsConfirmModal,
  IScanToEarnSettingsModal,
} from 'src/types'
import { TableHead } from './table-head'
import { TableBodyRow } from './table-row'
import { DeclineInfoModal } from 'src/components/consumer-scan-history/consumer-scans-list/decline-info-modal.component'
import { ScanToEarnSettingsModal } from './settings-modal.component'
import { ScanToEarnConfirmSettingsModal } from './settings-confirm-modal.component'
import { useCreateAIAccountAndProduct } from 'src/hooks/scan-to-earn/use-create-ai-account-and-product.hook'

interface IProps {
  data?: IConsumerScanRespDto[]
  sort: string
  setSort: Dispatch<SetStateAction<string>>
}

export const ScanToEarnManagementTable = ({ data = [], sort, setSort }: IProps) => {
  const [declineReason, setDeclineReason] = useState<null | string>(null)

  const { mutateAsync: createEntities, isLoading: isCreateEntitiesLoading } =
    useCreateAIAccountAndProduct()

  const [settingsModalData, setSettingsModalData] = useState<null | IScanToEarnSettingsModal>(null)
  const [settingsConfirmData, setSettingsConfirmData] =
    useState<null | IScanToEarnSettingsConfirmModal>(null)

  const handleSortChange = (sortName: string) => () => {
    setSort((prevSort) =>
      prevSort.split('-')[1] === 'asc' ? `${sortName}-desc` : `${sortName}-asc`,
    )
  }
  const activeSort = sort.split('-')[0]
  const sortDirection = sort.split('-')[1] as 'desc' | 'asc'

  const handleModalClose = () => {
    setDeclineReason(null)
  }

  const handleSettingsModalClose = () => {
    setSettingsModalData(null)
  }

  const handleDeclineInfoModalOpen = (declineReason: string) => {
    setDeclineReason(declineReason)
  }

  const handleSettingsModalOpen = (data: IScanToEarnSettingsModal) => {
    setSettingsModalData(data)
  }

  const handleConfirmSettingsModalClose = () => {
    setSettingsConfirmData(null)
  }

  const handleSettingsModalConfirm = async (data: IScanToEarnCreateAccountOrProductDto) => {
    await createEntities(data)

    setSettingsConfirmData(null)
  }

  const handleConfirmSettingsModalOpen = ({
    createProduct,
    createAccount,
    accountName,
    productName,
    consumerScanId,
    selectedAccountId,
  }: IScanToEarnSettingsConfirmModal) => {
    handleSettingsModalClose()
    setSettingsConfirmData({
      createProduct,
      createAccount,
      accountName,
      productName,
      consumerScanId,
      selectedAccountId,
    })
  }
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('laptop'))

  return (
    <>
      <TableContainer
        sx={
          isMobile
            ? {
                marginLeft: isMobile ? '-1.25rem' : 0,
                width: '100vw',
              }
            : {}
        }
      >
        <Table>
          <TableHead
            handleSortChange={handleSortChange}
            activeSort={activeSort}
            sortDirection={sortDirection}
          />
          <TableBody>
            {data?.map((row) => (
              <TableBodyRow
                item={row}
                key={row.id}
                isOpen={false}
                handleDeclineInfoModalOpen={handleDeclineInfoModalOpen}
                handleSettingsModalOpen={handleSettingsModalOpen}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <DeclineInfoModal
        declineReasonInfo={declineReason || ''}
        open={!!declineReason}
        handleClose={handleModalClose}
      />

      <ScanToEarnSettingsModal
        data={settingsModalData}
        open={!!settingsModalData}
        handleClose={handleSettingsModalClose}
        handleConfirm={handleConfirmSettingsModalOpen}
      />
      <ScanToEarnConfirmSettingsModal
        data={settingsConfirmData}
        open={!!settingsConfirmData}
        handleClose={handleConfirmSettingsModalClose}
        handleConfirm={handleSettingsModalConfirm}
        isLoading={isCreateEntitiesLoading}
      />
    </>
  )
}
